import { SituationDisposition, type SituationDetail } from "@mosaic/shared";
import {
  DISPOSITION_ACTION,
  DISPOSITION_BLURB,
  DISPOSITION_CHOICES,
  DISPOSITION_LABEL,
} from "../lib/format.js";

/**
 * The owner's judgement about a situation: the third axis, and the only one they set.
 *
 * `stage` is derived from evidence; pinning is attention; this is what the owner
 * concluded. The options want different behaviour from reconciliation, so each states
 * what it does to the pipeline rather than offering shades of "no".
 *
 * The owner writes no reason here. `disposition_reason` is still rendered below, because
 * automated paths write it (a read that returns `isSeparation: false`, and a merge), and
 * their reasoning is what a reader wants when the company turns up again.
 */

interface Props {
  situation: Pick<SituationDetail, "disposition" | "dispositionReason" | "dispositionAt">;
  onSet: (disposition: SituationDisposition) => void;
  pending?: boolean;
}

/**
 * The disposition controls for a situation, with the date and reason of the current one.
 *
 * @param onSet Called with a newly chosen disposition; never with the one already set.
 * @param pending Disables the controls while a write is in flight.
 */
export function DispositionPanel({ situation, onSet, pending = false }: Props) {
  function choose(next: SituationDisposition): void {
    // Every write lands on the timeline, so re-pressing the judgement already selected would
    // add entries saying nothing changed.
    if (next === situation.disposition) return;
    onSet(next);
  }

  return (
    <section className="disp" aria-label="Disposition">
      <div className="sechd">Disposition</div>

      <div className="disp-opts">
        {DISPOSITION_CHOICES.map((option) => (
          <button
            key={option}
            className="iconbtn disp-opt"
            aria-pressed={situation.disposition === option}
            disabled={pending}
            onClick={() => choose(option)}
          >
            <b>{DISPOSITION_ACTION[option]}</b>
            <span>{DISPOSITION_BLURB[option]}</span>
          </button>
        ))}
      </div>

      {/*
        The way back, only where there is something to go back from. The notice on a
        dismissed situation promises that "Disposition below returns it", and this is
        the control that keeps that promise.
      */}
      {situation.disposition !== "unreviewed" && (
        <div className="disp-opts disp-undo">
          <button
            className="iconbtn disp-opt"
            disabled={pending}
            onClick={() => choose("unreviewed")}
          >
            <b>{DISPOSITION_ACTION.unreviewed}</b>
            <span>{DISPOSITION_BLURB.unreviewed}</span>
          </button>
        </div>
      )}

      {situation.dispositionAt && (
        <p className="disp-meta">
          {DISPOSITION_LABEL[situation.disposition]} · {situation.dispositionAt.slice(0, 10)}
          {situation.dispositionReason ? ` · ${situation.dispositionReason}` : ""}
        </p>
      )}
    </section>
  );
}
